import type MapboxGL from 'mapbox-gl';
import type { ReactElement } from 'react';
import { cloneElement } from 'react';
import { createRoot } from 'react-dom/client';
import type { MarkerProps } from '../Map/Marker';
import { Marker, getMarkerSizeValues } from '../Map/Marker';

export interface Popup {
  createContent: () => ReactElement;
  options?: MapboxGL.PopupOptions;
}
const addPopup = (
  mapboxgl: typeof MapboxGL,
  marker: MapboxGL.Marker,
  createPopupContent: Popup['createContent'],
  options: Popup['options'] = {},
): void => {
  const popupInstance = new mapboxgl.Popup(options);

  let closePopup: () => void;

  const handleClose = (): void => {
    closePopup();
  };

  closePopup = () => marker.togglePopup();

  // Set Popup Content only when opened
  const setPopupContent = (): void => {
    const div = document.createElement('div');
    const root = createRoot(div);
    root.render(cloneElement(createPopupContent(), { onClose: handleClose }));
    popupInstance.setDOMContent(div);
  };

  const deletePopupContent = (): void => {
    popupInstance.setDOMContent(document.createElement('div'));
  };

  popupInstance.on('open', setPopupContent);
  popupInstance.on('close', deletePopupContent);

  marker.setPopup(popupInstance);
};

const cachedMap = new Map();

const cachedMarkerDom = (type: MarkerProps['type']): HTMLDivElement => {
  const html = cachedMap.get(type);
  const div = document.createElement('div');

  if (!html) {
    const root = createRoot(div);
    root.render(<Marker type={type} />);
    cachedMap.set(type, div.innerHTML);
  } else {
    div.innerHTML = html;
  }

  return div;
};

const internalAddMarker = (
  mapboxgl: typeof MapboxGL,
  map: MapboxGL.Map,
  lng: number,
  lat: number,
  type: MarkerProps['type'],
  popup?: Popup,
): MapboxGL.Marker => {
  const markerSize = getMarkerSizeValues();
  const marker = new mapboxgl.Marker({
    element: cachedMarkerDom(type),
    offset: [0, -(markerSize.height / 2)],
  })
    .setLngLat([lng, lat])
    .addTo(map);

  if (popup) {
    addPopup(mapboxgl, marker, popup.createContent, popup.options);
  }

  return marker;
};

export const addMarker = __TARGET__ === 'node' ? undefined : internalAddMarker;
